import React, { FC, FormEvent, useId, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { GuestShell } from '../components/guest-shell';
import { useSessionInfo } from '../session';
import { parseRoomInput } from '../lib/invite';
import { GUEST_ROUTES, joinPath } from '../routes.definitions';

/** Where a signed-in account lands — identical to the old `/` sign-in page. */
export const accountLandingPath = (user: {
  activatedAt: { Valid: boolean };
  isInRoom?: boolean;
}) => {
  if (!user.activatedAt.Valid) return '/verify-email';
  if (user.isInRoom) return '/in-play';
  return '/home';
};

const JoinByCode: FC = () => {
  const id = useId();
  const nav = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const roomID = parseRoomInput(value);
    if (!roomID) {
      setError('Paste the full invite link your friend sent.');
      return;
    }
    nav(joinPath(roomID));
  };

  return (
    <form onSubmit={submit} className="st-stack" noValidate>
      <label htmlFor={`${id}-code`} className="st-label">
        Got an invite link?
      </label>
      <div className="st-share-link">
        <input
          id={`${id}-code`}
          className="st-input st-input--dark"
          placeholder="Paste invite link"
          inputMode="url"
          autoComplete="off"
          value={value}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          onChange={e => {
            setValue(e.target.value);
            setError(null);
          }}
        />
        <button type="submit" className="st-btn st-btn--secondary">
          Join
        </button>
      </div>
      {error && (
        <span id={`${id}-err`} className="st-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
};

export const WelcomePage: FC = () => {
  const { user, isAccount, isGuest, name, isInRoom, guestExpired, bootError } =
    useSessionInfo();

  if (isAccount) {
    return <Navigate to={accountLandingPath(user)} replace />;
  }

  return (
    <GuestShell
      title="Play Bhabi with friends"
      className="st-welcome"
      right={
        <Link
          to="/sign-in"
          className="st-btn st-btn--ghost st-btn--small st-topbar__signin"
          aria-label={isGuest ? 'Sign in to your Bhabi account' : 'Sign in'}
        >
          Sign in
        </Link>
      }
    >
      <div className="st-stack" style={{ ['--st-gap' as string]: '22px' }}>
        {bootError === 'network' && (
          <div className="st-banner" role="alert">
            <span aria-hidden="true">⚠︎</span>
            <div className="st-stack" style={{ ['--st-gap' as string]: '8px' }}>
              <strong>We can&apos;t reach the Bhabi server.</strong>
              <span className="st-small st-muted">
                Your seat is saved on this device. Check your connection and try
                again.
              </span>
              <button
                type="button"
                className="st-btn st-btn--secondary st-btn--small"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {guestExpired && (
          <div className="st-banner st-banner--info" role="status">
            <span aria-hidden="true">⏳</span>
            <div>
              <strong>Your guest seat expired.</strong>{' '}
              <span className="st-small st-muted">
                Guest sessions last 7 days. Start a new table or join one, and
                we&apos;ll keep your name{name ? ` (${name})` : ''} ready.
                Earlier tables can&apos;t be reopened.
              </span>
            </div>
          </div>
        )}

        <section className="st-hero st-stack" aria-labelledby="hero-title">
          <div className="st-fan" aria-hidden="true">
            <div className="st-fan__card">
              <span>A</span>♠
            </div>
            <div className="st-fan__card st-fan__card--red">
              <span>K</span>♥
            </div>
            <div className="st-fan__card">
              <span>Q</span>♣
            </div>
            <div className="st-fan__card st-fan__card--red">
              <span>J</span>♦
            </div>
          </div>
          <p className="st-eyebrow">The Punjabi card game</p>
          <h1 id="hero-title" className="st-display">
            {isGuest ? (
              <>
                Sat sri akal, <em>{name}</em>
              </>
            ) : (
              <>
                Don&apos;t be the <em>Bhabi.</em>
              </>
            )}
          </h1>
          <p className="st-lead">
            Start a private table, send one link, play in seconds. No sign-up,
            no downloads.
          </p>
        </section>

        <section className="st-stack" aria-label="Play">
          {isGuest && isInRoom ? (
            <>
              <Link
                to={GUEST_ROUTES.TABLE}
                className="st-btn st-btn--primary st-btn--block"
              >
                Return to your table
              </Link>
              <p className="st-small st-muted">
                You&apos;re still seated at a table. Leave it from the table
                screen to start a new one.
              </p>
            </>
          ) : (
            <Link
              to={GUEST_ROUTES.NEW_TABLE}
              className="st-btn st-btn--primary st-btn--block"
            >
              Start a private table
            </Link>
          )}
          <div className="st-divider">or</div>
          <JoinByCode />
        </section>

        <Link
          to="/game-rules"
          className="st-btn st-btn--secondary st-btn--block"
        >
          How to play
        </Link>

        <footer
          className="st-stack st-small st-muted"
          style={{ ['--st-gap' as string]: '8px' }}
        >
          <p>
            <Link className="st-link" to="/toc">
              Terms &amp; privacy
            </Link>
          </p>
        </footer>
      </div>
    </GuestShell>
  );
};
